import React, { useMemo, useState } from "react";
import { useGetAdminTicketsQuery, useGetAdminTicketsStatsQuery, useAwardTicketsMutation } from "../api";
import { UserAutocomplete, User } from "../components/UserAutocomplete";
import { AdminTicketsParams } from "../api/tickets";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

export const AdminTicketsPage: React.FC = () => {
    const { showError, showSuccess, notification, hideNotification } = useNotification();

    // Award form
    const [awardUserId, setAwardUserId] = useState("");
    const [awardUser, setAwardUser] = useState<User | null>(null);
    const [awardType, setAwardType] = useState<"purchased" | "gift">("purchased");
    const [awardQuantity, setAwardQuantity] = useState(1);

    // Filters
    const [page, setPage] = useState(1);
    const [userId, setUserId] = useState("");
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isGift, setIsGift] = useState<"" | "true" | "false">("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const params = useMemo<AdminTicketsParams>(() => {
        return {
            page,
            per_page: 15,
            sort_by: "alny_tickets_auto_id",
            sort_order: "desc",
            user_id: userId ? parseInt(userId) : undefined,
            is_gift: isGift === "" ? undefined : isGift === "true",
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
        };
    }, [page, userId, isGift, dateFrom, dateTo]);

    const {
        data: ticketsResponse,
        isLoading: isLoadingTickets,
    } = useGetAdminTicketsQuery(params);

    const { data: statsResponse } = useGetAdminTicketsStatsQuery();
    const stats = statsResponse?.data;

    const [awardTicketsMutation, { isLoading: isAwarding }] = useAwardTicketsMutation();

    const handleAwardSubmit = async () => {
        const uid = awardUser?.id ?? (awardUserId ? parseInt(awardUserId, 10) : null);
        if (!uid || Number.isNaN(uid)) {
            showError("Выберите пользователя");
            return;
        }
        if (awardQuantity < 1 || awardQuantity > 1000) {
            showError("Количество билетов от 1 до 1000");
            return;
        }
        try {
            const res = await awardTicketsMutation({
                user_id: uid,
                is_gift: awardType === "gift",
                quantity: awardQuantity,
            }).unwrap();
            showSuccess(res.message ?? "Билеты начислены");
            setAwardQuantity(1);
            setAwardUser(null);
            setAwardUserId("");
        } catch (err: any) {
            showError(err?.data?.message ?? "Ошибка при начислении билетов");
        }
    };

    const tickets = ticketsResponse?.data ?? [];
    const ticketsMeta = ticketsResponse?.meta;

    const handleUserSelect = (user: User) => {
        setSelectedUser(user);
        setUserId(user.id.toString());
        setPage(1);
    };

    const handleUserClear = () => {
        setSelectedUser(null);
        setUserId("");
        setPage(1);
    };

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Билеты лотереи
                </h1>
            </div>

            {/* Блоки статистики */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Лимит
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                        {stats?.limit ?? "—"}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Осталось
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                        {stats?.remaining ?? "—"}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Купленных
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-blue-600 dark:text-blue-400">
                        {stats?.purchased_count ?? "—"}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Подарочных
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-green-600 dark:text-green-400">
                        {stats?.gift_count ?? "—"}
                    </div>
                </div>
            </div>

            {/* Форма начисления билетов */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Начислить билеты
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Пользователь
                        </label>
                        <UserAutocomplete
                            value={awardUserId}
                            onChange={(value) => {
                                if (!value) {
                                    setAwardUser(null);
                                    setAwardUserId("");
                                } else {
                                    setAwardUserId(value);
                                }
                            }}
                            onUserSelect={(user) => {
                                setAwardUser(user);
                                setAwardUserId(user.id.toString());
                            }}
                            placeholder="ID, email, phone, name..."
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Тип билета
                        </label>
                        <select
                            value={awardType}
                            onChange={(e) =>
                                setAwardType(e.target.value as "purchased" | "gift")
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        >
                            <option value="purchased">Купленный</option>
                            <option value="gift">Подарочный</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Количество
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={1000}
                            value={awardQuantity}
                            onChange={(e) =>
                                setAwardQuantity(
                                    Math.min(
                                        1000,
                                        Math.max(1, parseInt(e.target.value, 10) || 1)
                                    )
                                )
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <button
                            type="button"
                            onClick={handleAwardSubmit}
                            disabled={isAwarding}
                            className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-md"
                        >
                            {isAwarding ? "Начисление…" : "Начислить"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Пользователь
                        </label>
                        <UserAutocomplete
                            value={userId}
                            onChange={(value) => {
                                if (!value) {
                                    handleUserClear();
                                } else {
                                    setUserId(value);
                                    setPage(1);
                                }
                            }}
                            onUserSelect={handleUserSelect}
                            placeholder="ID, email, phone, name..."
                            className="w-full"
                        />
                        {selectedUser && (
                            <button
                                onClick={handleUserClear}
                                className="mt-1 text-xs text-red-600 dark:text-red-400 hover:underline"
                            >
                                Очистить
                            </button>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Тип билета
                        </label>
                        <select
                            value={isGift}
                            onChange={(e) => {
                                setIsGift(e.target.value as "" | "true" | "false");
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все</option>
                            <option value="false">Купленные</option>
                            <option value="true">Подарочные</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата от
                        </label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата до
                        </label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                </div>
            </div>

            {/* Tickets Table */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    ID
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Пользователь
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Дата создания
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Тип
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {isLoadingTickets ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                    >
                                        Загрузка...
                                    </td>
                                </tr>
                            ) : tickets.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                    >
                                        Билеты не найдены
                                    </td>
                                </tr>
                            ) : (
                                tickets.map((ticket) => (
                                    <tr
                                        key={ticket.id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {ticket.id}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {ticket.user ? (
                                                <div>
                                                    <div className="font-medium">
                                                        {ticket.user.name} {ticket.user.surname}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        ID: {ticket.user.id}
                                                        {ticket.user.email && (
                                                            <> • {ticket.user.email}</>
                                                        )}
                                                        {ticket.user.mobile && (
                                                            <> • {ticket.user.mobile}</>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">
                                                    Пользователь не найден (ID: {ticket.user_id})
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {ticket.created_at}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            {ticket.is_gift ? (
                                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                    Подарок
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                    Куплен
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {ticketsMeta && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Показано {ticketsMeta.from ?? 0}–{ticketsMeta.to ?? 0} из{" "}
                            {ticketsMeta.total}
                        </div>
                        <div className="flex gap-2">
                            <button
                                disabled={ticketsMeta.current_page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Назад
                            </button>
                            <button
                                disabled={
                                    ticketsMeta.current_page >= ticketsMeta.last_page
                                }
                                onClick={() =>
                                    setPage((p) =>
                                        Math.min(ticketsMeta.last_page, p + 1)
                                    )
                                }
                                className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Вперёд
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
        </div>
    );
};
